import { useCallback, useState, type KeyboardEvent } from 'react';

export type NumberDraftOpts = {
  /** The committed value the box shows while the user is not editing. */
  value: number | string;
  /** Floor the committed value snaps to; the hint names it. */
  min: number;
  /** Truncate a decimal draft to a whole number before clamping. */
  integer?: boolean;
  /** What an empty box commits as. Defaults to `min`, and then counts as a
   *  clamp (the hint shows); set it to the floor itself for a box where empty
   *  legitimately means "none", e.g. a capacity of 0. */
  emptyAs?: number;
  onCommit: (n: number) => void;
};

export type NumberDraft = {
  value: number | string;
  onChange: (raw: string) => void;
  onBlur: () => void;
  onKeyDown: (e: KeyboardEvent<HTMLInputElement>) => void;
  /** "min N" while the draft is below the floor, and after a commit clamped
   *  it; null otherwise. */
  hint: string | null;
};

const parse = (raw: string, opts: NumberDraftOpts): { parsed: number; fromEmpty: boolean } => {
  if (raw.trim() === '') return { parsed: opts.emptyAs ?? NaN, fromEmpty: true };
  const n = Number(raw);
  return { parsed: opts.integer ? Math.trunc(n) : n, fromEmpty: false };
};

/** CONF-217: a free-text numeric box that is clamped on every keystroke cannot
 *  take "13" when the floor is 3 — the leading "1" snaps back — and a box that
 *  commits on every keystroke re-runs rules → metrics → pricing → BOM per key.
 *  So the keystrokes are held here as a string draft; the number is clamped and
 *  committed once, on blur or Enter, and a clamp is said out loud through
 *  `hint` instead of silently rewriting what was typed. */
export function useNumberDraft(opts: NumberDraftOpts): NumberDraft {
  const { value, min, onCommit } = opts;
  const [draft, setDraft] = useState<string | null>(null);
  const [clamped, setClamped] = useState(false);

  const onChange = useCallback((raw: string) => {
    setDraft(raw);
    setClamped(false);
  }, []);

  const commit = useCallback(() => {
    if (draft === null) return;
    const { parsed } = parse(draft, opts);
    const below = !(parsed >= min); // NaN counts as below
    const n = below ? min : parsed;
    setClamped(below);
    setDraft(null);
    if (n !== Number(value)) onCommit(n);
  }, [draft, opts, min, value, onCommit]);

  const onKeyDown = useCallback((e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') commit();
  }, [commit]);

  const liveBelow = draft !== null && !(parse(draft, opts).parsed >= min);
  const hint = liveBelow || clamped ? `min ${min}` : null;

  return { value: draft ?? value, onChange, onBlur: commit, onKeyDown, hint };
}
